import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { API_URL } from "./config";

const PAGE_SIZES = [10, 20, 50, 100];
const SEMESTERS = [11, 12, 21, 22, 31, 32, 41, 42];

const HostelStudents = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const sectionSx = { ...surfaceSx, p: 2 };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
  });
  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  // Header checkbox sits on the blue table header, so give it its own
  // high-contrast treatment instead of using the normal blue checkbox.
  const headerCheckboxSx = {
    color: "#FFFFFF",
    borderRadius: 1,
    p: 0.45,
    backgroundColor: "rgba(255,255,255,0.12)",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.22)",
    },
    "&.Mui-checked": {
      color: THEME_ORANGE,
      backgroundColor: "#FFFFFF",
    },
    "&.MuiCheckbox-indeterminate": {
      color: THEME_ORANGE,
      backgroundColor: "#FFFFFF",
    },
    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.45)",
      backgroundColor: "rgba(255,255,255,0.08)",
    },
  };

  const [students, setStudents] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [bulkReassignRoom, setBulkReassignRoom] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [filterSemester, setFilterSemester] = useState("");
  const [filterSubCampus, setFilterSubCampus] = useState("");
  const [filterBlock, setFilterBlock] = useState("");
  const [filterRoom, setFilterRoom] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filterOptions, setFilterOptions] = useState({
    subCampuses: [],
    blocks: [],
    rooms: [],
  });

  const token = localStorage.getItem("auth");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    filterStatus,
    filterGender,
    filterSemester,
    filterSubCampus,
    filterBlock,
    filterRoom,
    pageSize,
  ]);

  const fetchStudents = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        pageSize,
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (filterStatus !== "") params.IsStudentActive = filterStatus === "1";
      if (filterGender) params.Gender = filterGender;
      if (filterSemester) params.Semester = filterSemester;
      if (filterSubCampus) params.SubCampusCode = filterSubCampus;
      if (filterBlock) params.Block = filterBlock;
      if (filterRoom) params.RoomNumber = filterRoom;

      const response = await axios.get(`${API_URL}/api/getHostelStudents`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // New paginated contract. The fallback keeps the page usable briefly
      // if an older backend is still running during deployment.
      const payload = response.data;
      if (Array.isArray(payload)) {
        setStudents(payload);
        setTotalCount(payload.length);
        setTotalPages(1);
        setFilterOptions({
          subCampuses: [...new Set(payload.map((s) => s.SubCampusCode).filter(Boolean))],
          blocks: [...new Set(payload.map((s) => s.Block).filter(Boolean))],
          rooms: [...new Set(payload.map((s) => s.RoomNumber).filter(Boolean))],
        });
      } else {
        const rows = Array.isArray(payload?.students) ? payload.students : [];
        const nextTotalPages = Math.max(1, Number(payload?.pagination?.totalPages ?? 1));
        setStudents(rows);
        setTotalCount(Number(payload?.pagination?.totalCount ?? 0));
        setTotalPages(nextTotalPages);
        setFilterOptions({
          subCampuses: Array.isArray(payload?.filterOptions?.subCampuses)
            ? payload.filterOptions.subCampuses
            : [],
          blocks: Array.isArray(payload?.filterOptions?.blocks)
            ? payload.filterOptions.blocks
            : [],
          rooms: Array.isArray(payload?.filterOptions?.rooms)
            ? payload.filterOptions.rooms
            : [],
        });
        if (page > nextTotalPages) setPage(nextTotalPages);
      }
    } catch (err) {
      console.error("Error fetching students:", err);
      setStudents([]);
      setError(err?.response?.data?.error || "Failed to fetch students");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableRooms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/availableRooms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableRooms(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching available rooms:", err);
      setError("Failed to fetch available rooms.");
    }
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    debouncedSearch,
    filterStatus,
    filterGender,
    filterSemester,
    filterSubCampus,
    filterBlock,
    filterRoom,
  ]);

  useEffect(() => {
    fetchAvailableRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageIds = useMemo(() => students.map((student) => student.id), [students]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedStudentIds.includes(id));

  const handleSelectStudent = (id) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedStudentIds((prev) => {
      if (allPageSelected) return prev.filter((id) => !pageIds.includes(id));
      return [...new Set([...prev, ...pageIds])];
    });
  };

  const removeSelectedStudents = async () => {
    if (!selectedStudentIds.length) return;
    try {
      await axios.post(
        `${API_URL}/api/removeSelectedStudents`,
        { id: selectedStudentIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setSelectedStudentIds([]);
      await Promise.all([fetchStudents(), fetchAvailableRooms()]);
    } catch (err) {
      console.error("Error removing students:", err);
      setError(err?.response?.data?.error || "Failed to remove students");
    }
  };

  const reassignSelectedStudents = async () => {
    if (!bulkReassignRoom) {
      setError("Please select a room before reassigning.");
      return;
    }
    if (!selectedStudentIds.length) return;

    try {
      await axios.post(
        `${API_URL}/api/bulkReassignHostelRoom`,
        {
          studentIds: selectedStudentIds,
          newRoomId: bulkReassignRoom,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setSelectedStudentIds([]);
      setBulkReassignRoom("");
      await Promise.all([fetchStudents(), fetchAvailableRooms()]);
    } catch (err) {
      console.error("Error reassigning students:", err);
      setError(err?.response?.data?.error || "Failed to reassign students.");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setFilterStatus("");
    setFilterGender("");
    setFilterSemester("");
    setFilterSubCampus("");
    setFilterBlock("");
    setFilterRoom("");
    setPage(1);
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
            Hostel Management
          </Typography>
          <Typography sx={{ opacity: 0.9, fontSize: "0.85rem", mt: 0.35 }}>
            Filter students, reassign rooms, or remove allocations
          </Typography>
        </Box>

        <Box sx={bodySx}>
          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
            <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccess(false)}>
              Operation successful!
            </Alert>
          </Snackbar>

          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>Filters</Typography>
              <Button size="small" onClick={clearFilters} sx={{ textTransform: "none", fontWeight: 800 }}>
                Clear
              </Button>
            </Box>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Grid container spacing={1.2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Name, Student ID, block or room"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Student Status</InputLabel>
                  <Select value={filterStatus} label="Student Status" onChange={(e) => setFilterStatus(e.target.value)} sx={inputSx}>
                    <MenuItem value="">All Students</MenuItem>
                    <MenuItem value="1">Active Students</MenuItem>
                    <MenuItem value="0">Inactive Students</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gender</InputLabel>
                  <Select value={filterGender} label="Gender" onChange={(e) => setFilterGender(e.target.value)} sx={inputSx}>
                    <MenuItem value="">All Genders</MenuItem>
                    <MenuItem value="M">Male</MenuItem>
                    <MenuItem value="F">Female</MenuItem>
                    <MenuItem value="O">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Semester</InputLabel>
                  <Select value={filterSemester} label="Semester" onChange={(e) => setFilterSemester(e.target.value)} sx={inputSx}>
                    <MenuItem value="">All Semesters</MenuItem>
                    {SEMESTERS.map((sem) => <MenuItem key={sem} value={String(sem)}>{sem}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sub Campus</InputLabel>
                  <Select value={filterSubCampus} label="Sub Campus" onChange={(e) => setFilterSubCampus(e.target.value)} sx={inputSx}>
                    <MenuItem value="">All Sub Campuses</MenuItem>
                    {filterOptions.subCampuses.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Block</InputLabel>
                  <Select value={filterBlock} label="Block" onChange={(e) => setFilterBlock(e.target.value)} sx={inputSx}>
                    <MenuItem value="">All Blocks</MenuItem>
                    {filterOptions.blocks.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Room</InputLabel>
                  <Select value={filterRoom} label="Room" onChange={(e) => setFilterRoom(e.target.value)} sx={inputSx}>
                    <MenuItem value="">All Rooms</MenuItem>
                    {filterOptions.rooms.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Reassign or Delete Selected Students
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 280, flex: 1 }}>
                <InputLabel>Select Room</InputLabel>
                <Select value={bulkReassignRoom} label="Select Room" onChange={(e) => setBulkReassignRoom(e.target.value)} sx={inputSx}>
                  <MenuItem value=""><em>Select Room</em></MenuItem>
                  {availableRooms.map((room) => (
                    <MenuItem key={room.RoomID} value={room.RoomID}>
                      {room.Block}-Block {room.RoomNumber} {room.RoomType} ({room.IsAirConditioned === "Yes" ? "AC" : "NonAC"}) (Cap: {room.Capacity}) (Occ: {room.Occupied})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button variant="contained" sx={{ ...primaryBtnSx, minWidth: 190 }} onClick={reassignSelectedStudents} disabled={!selectedStudentIds.length}>
                Reassign Selected ({selectedStudentIds.length})
              </Button>
              <Button variant="contained" color="error" sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900, minWidth: 180 }} onClick={removeSelectedStudents} disabled={!selectedStudentIds.length}>
                Remove Selected
              </Button>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...surfaceSx, mt: 2, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1.25, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>Students</Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                  {totalCount} result(s) • Page {page} of {totalPages}
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 135 }}>
                <InputLabel>Rows per page</InputLabel>
                <Select value={pageSize} label="Rows per page" onChange={(e) => setPageSize(Number(e.target.value))} sx={inputSx}>
                  {PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <TableContainer component="div">
                <Table size="small" sx={{ minWidth: 1180 }}>
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell>#</TableCell>
                      <TableCell>Sub Campus</TableCell>
                      <TableCell>Student ID</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Semester</TableCell>
                      <TableCell>Block</TableCell>
                      <TableCell>Room Number</TableCell>
                      <TableCell>Room Type</TableCell>
                      <TableCell>AC</TableCell>
                      <TableCell>Capacity</TableCell>
                      <TableCell>Occupied</TableCell>
                      <TableCell>Is Student Active</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          sx={headerCheckboxSx}
                          checked={allPageSelected}
                          onChange={handleSelectAll}
                          disabled={!students.length}
                          inputProps={{ "aria-label": "Select all students on this page" }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((student, index) => (
                      <TableRow key={student.id} sx={zebraRowSx(index)}>
                        <TableCell>{(page - 1) * pageSize + index + 1}</TableCell>
                        <TableCell>{student.SubCampusCode}</TableCell>
                        <TableCell>{student.StudentID}</TableCell>
                        <TableCell>{student.FullName}</TableCell>
                        <TableCell>{student.Gender}</TableCell>
                        <TableCell>{student.Semester}</TableCell>
                        <TableCell>{student.Block}</TableCell>
                        <TableCell>{student.RoomNumber}</TableCell>
                        <TableCell>{student.RoomType}</TableCell>
                        <TableCell>{String(student.IsAirConditioned ?? "")}</TableCell>
                        <TableCell>{student.Capacity}</TableCell>
                        <TableCell>{student.Occupied}</TableCell>
                        <TableCell>{student.IsStudentActive ? "Yes" : "No"}</TableCell>
                        <TableCell align="center">
                          <Checkbox sx={checkboxSx} checked={selectedStudentIds.includes(student.id)} onChange={() => handleSelectStudent(student.id)} />
                        </TableCell>
                      </TableRow>
                    ))}

                    {!loading && students.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={14} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                          No students found for the selected filters.
                        </TableCell>
                      </TableRow>
                    )}
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={14} sx={{ py: 3, textAlign: "center", opacity: 0.7 }}>
                          Loading students…
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "center", borderTop: `1px solid ${BORDER}` }}>
              <Pagination
                page={page}
                count={totalPages}
                onChange={(_, value) => setPage(value)}
                disabled={loading}
                showFirstButton
                showLastButton
                color="primary"
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default HostelStudents;
